import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Tooltip,
    Button,
    TextField,
    Box,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
    getComparator,
    stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import {
    GetAllEmailTemplates,
    DeleteEmailTemplate,
} from "../../../services/EmailTemplatesService";
import ViewEmailTemplate from "./ViewEmailTemplate";
import CommonLoader from "../../common/CommonLoader";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const EmailTemplatesTable = () => {
    const columns = [
        { id: "id", label: "ID" },
        { id: "type", label: "Type" },
        { id: "subjectLine", label: "Subject Line" },
        { id: "fromAddress", label: "From Address" },
        { id: "actions", label: "Actions" },
    ];

    const rowsPerPageOptions = [10, 25, 50];

    const [templatesData, setTemplatesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState("asc");
    const [searchText, setSearchText] = useState("");
    const navigate = useNavigate();

    // Template type mapping aligned with backend EmailType enum
    const templateTypeMap = {
        0: "Not Specified",
        1: "Drinking Water",
        2: "Drinking Water (Non-Regulated)",
        3: "Waste Water",
        4: "Indoor Pool",
        5: "Outdoor Pool",
        6: "Multiple",
        7: "ETK",
        8: "ETK Distributor",
        9: "ETK Wholesale",
        10: "Bathing Beach",
        11: "Bottle Order Sent",
        12: "ETK Received",
        13: "Invoice Sent",
        14: "Sample Receipt",
        15: "Quote",
        16: "Rating"
    };


    useEffect(() => {
        fetchTemplates();
    }, [isDeleted]);

    useEffect(() => {
        // Filter data based on search text
        if (searchText.trim() === "") {
            setFilteredData(templatesData);
        } else {
            const searchLower = searchText.toLowerCase();
            const filtered = templatesData.filter((template) => {
                const typeLabel = templateTypeMap[template.type] || "";
                return (
                    typeLabel.toLowerCase().includes(searchLower) ||
                    (template.subjectLine && template.subjectLine.toLowerCase().includes(searchLower)) ||
                    (template.fromAddress && template.fromAddress.toLowerCase().includes(searchLower)) ||
                    (template.bodyHtml && template.bodyHtml.toLowerCase().includes(searchLower))
                );
            });
            setFilteredData(filtered);
        }
        setPage(0); // Reset to first page when searching
    }, [searchText, templatesData]);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await GetAllEmailTemplates("");
            const templates = response.results || [];
            setTemplatesData(templates);
            setFilteredData(templates);
        } catch (error) {
            console.error("Error fetching templates:", error);
            NotificationManager.error("Error fetching templates");
        } finally {
            setLoading(false);
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewClick = (template) => setSelectedTemplate(template);

    const handleBackClick = () => {
        setSelectedTemplate(null);
    };

    const handleDeleteClick = (template) => {
        setSelectedTemplate(template);
        setIsDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmationClose = () => {
        setIsDeleteConfirmationOpen(false);
        setSelectedTemplate(null);
    };

    const handleDeleteConfirmationConfirm = async () => {
        try {
            setLoading(true);
            const response = await DeleteEmailTemplate(selectedTemplate.id);
            if (response.results === true) {
                NotificationManager.success("Template deleted successfully", "Success");
                setIsDeleted(!isDeleted);
            } else {
                NotificationManager.error("Error deleting template", "Error");
            }
        } catch (error) {
            NotificationManager.error("Error deleting template", "Error");
        } finally {
            setLoading(false);
            setSelectedTemplate(null);
            setIsDeleteConfirmationOpen(false);
        }
    };

    const handleEditTemplateClick = (template) =>
        navigate(`/email-templates/${template.id}`);

    const handleAddTemplateClick = () => navigate("/email-templates/add");

    const renderActions = (template) => (
        <div style={{ minWidth: "120px", display: "flex" }}>
            <Tooltip title="Edit Template">
                <IconButton
                    aria-label="edit"
                    style={{ color: "orange" }}
                    onClick={() => handleEditTemplateClick(template)}
                >
                    <EditIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="View Template">
                <IconButton
                    aria-label="view"
                    style={{ color: "green" }}
                    onClick={() => handleViewClick(template)}
                >
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Template">
                <IconButton
                    aria-label="delete"
                    style={{ color: "red" }}
                    onClick={() => handleDeleteClick(template)}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </div>
    );

    return (
        <>
            <span className="title">Email Templates</span>
            <hr />
            {selectedTemplate ? (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackClick}
                        style={{ marginBottom: "16px", color: "green" }}
                    >
                        Back
                    </Button>
                    <ViewEmailTemplate template={selectedTemplate} />
                </>
            ) : (
                <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", marginRight: "30px" }}>
                        <TextField
                            label="Search Templates"
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon style={{ marginRight: "8px", color: "gray" }} />,
                            }}
                            sx={{ width: "300px" }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleAddTemplateClick}
                        >
                            Add Template
                        </Button>
                    </Box>

                    <Paper style={{ marginRight: "20px" }}>
                        <TableContainer
                            sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}
                        >
                            <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
                                <TableHead
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.primary.main,
                                        color: "white",
                                    }}
                                >
                                    <TableRow>
                                        {columns.map((col) => (
                                            <TableCell key={col.id}>
                                                {col.id === "actions" ? (
                                                    col.label
                                                ) : (
                                                    <TableSortLabel
                                                        active={orderBy === col.id}
                                                        direction={orderBy === col.id ? order : "asc"}
                                                        onClick={() => handleRequestSort(col.id)}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {stableSort(filteredData, getComparator(order, orderBy))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((template, index) => (
                                            <TableRow
                                                key={template.id}
                                                sx={{
                                                    backgroundColor:
                                                        index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                                                }}
                                            >
                                                <TableCell>{template.id || "N/A"}</TableCell>
                                                <TableCell>{templateTypeMap[template.type] || "N/A"}</TableCell>
                                                <TableCell>{template.subjectLine || "N/A"}</TableCell>
                                                <TableCell>{template.fromAddress || "N/A"}</TableCell>
                                                <TableCell>{renderActions(template)}</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={rowsPerPageOptions}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteConfirmationOpen}
                onClose={handleDeleteConfirmationClose}
                onConfirm={handleDeleteConfirmationConfirm}
                message="Are you sure you want to delete this email template?"
            />

            <CommonLoader loading={loading} />
        </>
    );
};

export default EmailTemplatesTable;